import { useCallback, useEffect, useState } from 'react'
import useSwipe from '../../hooks/useSwipe'
import useDoc, { Document, Word } from '../../hooks/useDoc'
import useVocab, {
  Vocab,
  defineWord,
} from '../../hooks/useVocab'
import useSession, { Session } from '../../hooks/useSession'
import SimpleLayout from '../../layouts/SimpleLayout'
import VocabSheet from '../../components/VocabSheet'
import VocabTooltip from '../../components/VocabTooltip'
import Page from '../../components/Page'
import useAuth from 'src/hooks/useAuth'
import { LanguageCode } from 'src/models/language'
import Spinner from 'src/components/Spinner'
import PageTurner from 'src/components/PageTurner'
import Modal from 'src/components/Modal'
import useOrg, {
  getGroupAndGroupIdFromDocumentId,
} from 'src/hooks/useOrg'
import OrgBanner from 'src/components/OrgBanner'
import { Tabs, TabsContent } from 'src/components/ui/Tabs'
import { DocumentSession } from 'src/hooks/useSessions'

const DEMO_SESSION: DocumentSession = {
  id: 'public:demo:don_quixote_volume1',
  documentId: 'public:demo:don_quixote_volume1',
  title: 'Don Quixote Volume 1',
  author: 'Miguel de Cervantes',
  lang: 'es-es',
  group: 'public',
  groupId: 'demo',
  toLangs: ['en-us'],
  characterNum: 1,
  fromLang: LanguageCode.SpanishSpain,
  toLang: LanguageCode.EnglishUs,
  difficulty: 2,
  numChapters: 25,
};

const Options = ({
  doc,
  onChapterNumChange,
}: {
  doc: Document
  onChapterNumChange: (chapterNum: number) => void
}) => {
  const [group, groupId] = doc.id
    ? getGroupAndGroupIdFromDocumentId(doc.id)
    : [undefined, undefined]
  const { org } = useOrg(
    group === 'orgs' ? groupId : undefined,
  )

  return (
    <div>
      {org && (
        <div className="-mb-2 -translate-y-8">
          <OrgBanner {...org} />
          <div className="absolute top-0 right-0 h-8 w-8 rounded-bl-md bg-white" />
        </div>
      )}
      <Tabs defaultValue="chapters">
        {/* <TabsList>
          <TabsTrigger value="chapters">
            Chapters
          </TabsTrigger>
        </TabsList> */}
        <TabsContent value="chapters">
          <div className="flex flex-col space-y-4">
            {doc.index.map(({ title, chapterNum }) => (
              <button
                // className="text-left"
                key={chapterNum}
                onClick={() =>
                  onChapterNumChange(chapterNum)
                }
              >
                <span className="border-b border-b-transparent py-2 text-left transition-all ease-in-out hover:border-b-black">
                  {title ?? `Chapter ${chapterNum}`}
                </span>
              </button>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

const Reader = ({
  session,
  save,
}: {
  session: Session
  save: (s: Partial<Session>) => void
}) => {
  const [lang, setLang] = useState(session.fromLang)
  const isTranslation = lang !== session.fromLang

  useEffect(() => {
    if (!session.fromLang) return
    setLang(session.fromLang)
  }, [session.fromLang])

  const [vocabEl, setVocabEl] = useState<
    HTMLElement | undefined
  >()
  const [isVocabSheetVisible, setIsVocabSheetVisible] =
    useState(false)
  const [isOptionsVisible, setIsOptionsVisible] =
    useState(false)

  const {
    doc,
    pageNum,
    onPageNumChange,
    onChapterNumChange,
    words,
    translation,
    isLoading: isDocLoading,
    characterNum,
  } = useDoc({
    documentId: session.documentId,
    characterNum: session.characterNum,
    fromLang: session.fromLang,
    toLang: session.toLang,
  })

  const setVocab = useCallback(
    (
      cb: (v: Vocab[] | undefined) => Vocab[] | undefined,
    ) => {
      const updatedVocab = cb(session?.vocab)
      save({ vocab: updatedVocab })
    },
    [save, session?.vocab],
  )

  const {
    add: handleAddVocab,
    remove: handleRemoveVocab,
    define: handleDefineVocab,
    vocabWord,
    setVocabWord,
    vocab,
    isLoading: isVocabLoading,
  } = useVocab({
    ...session,
    setVocab,
  })

  const direction = useSwipe()

  const closeVocab = useCallback(() => {
    setVocabWord(undefined)
    setVocabEl(undefined)
  }, [setVocabWord])

  const handleDefine = useCallback(
    (event: any, word: Word) => {
      setVocabEl(event.target)
      handleDefineVocab(word)
    },
    [handleDefineVocab],
  )

  const handleChapterNumChange = useCallback(
    (chapterNum: number) => {
      onChapterNumChange(chapterNum)
      setIsOptionsVisible(false)
      setIsVocabSheetVisible(false)
    },
    [onChapterNumChange],
  )

  const handleFlip = useCallback(() => {
    if (lang === session.fromLang) {
      setLang(session.toLang)
      closeVocab()
      return
    }
    setLang(session.fromLang)
  }, [
    setLang,
    closeVocab,
    lang,
    session.fromLang,
    session.toLang,
  ])

  const handlePageNumChange = useCallback(
    (delta: number) => {
      const characterNum = onPageNumChange(delta)

      if (
        characterNum === null ||
        characterNum === undefined
      )
        return

      const wordCounts = (words || []).reduce(
        (acc, word) => {
          acc[word.clean] = acc[word.clean]
            ? acc[word.clean] + 1
            : 1
          return acc
        },
        {} as Record<string, number>,
      )

      save({
        characterNum,
        vocab: (session.vocab || []).map((v) => {
          const count = wordCounts[v.word.clean] ?? 0
          return {
            ...v,
            numEncounters: (v.numEncounters || 1) + count,
          }
        }),
      })
    },
    [onPageNumChange, save, session.vocab, words],
  )

  const handleVocabCheck = useCallback(
    (event: any, word: Word, vocab?: Vocab[]) => {
      setVocabEl(event.target)
      handleDefineVocab(word)
    },
    [handleDefineVocab],
  )

  // Turn page on swipe
  useEffect(() => {
    if (
      direction &&
      Math.abs(direction) > 80 &&
      !isTranslation
    ) {
      handlePageNumChange(Math.sign(direction))
    }
  }, [
    direction,
    handlePageNumChange,
    isTranslation,
    pageNum,
    save,
  ])

  return (
    <>
      <div className="display-none sm:display-block absolute left-1/4 top-1/2 -ml-8 transition ease-in-out hover:-translate-x-2 sm:z-50 lg:left-[25%] lg:top-[calc(50%-3rem)] lg:cursor-pointer lg:p-12">
        <PageTurner
          onClick={() =>
            !isTranslation && handlePageNumChange(-1)
          }
        >
          ←
        </PageTurner>
      </div>

      <div className="display-none sm:display-block absolute right-1/4 top-1/2 -mr-8 transition ease-in-out hover:translate-x-2 sm:z-50 lg:right-[25%] lg:top-[calc(50%-3rem)] lg:cursor-pointer lg:p-12">
        <PageTurner
          onClick={() =>
            !isTranslation && handlePageNumChange(1)
          }
        >
          →
        </PageTurner>
      </div>

      <main className="relative h-full max-h-full w-full max-w-full flex-grow overflow-hidden rounded-xl sm:max-h-[45rem] sm:max-w-sm">
        <Page
          lang={lang}
          isLoading={isDocLoading}
          document={doc}
          words={words}
          vocab={vocab}
          translation={translation}
          numVocab={vocab?.length ?? 0}
          fromLang={session.fromLang}
          toLang={session.toLang}
          pageNum={pageNum}
          characterNum={characterNum}
          onPageNumChange={handlePageNumChange}
          onWordClick={handleDefine}
          onVocabCheck={handleVocabCheck}
          onFlip={handleFlip}
          onOptions={() => setIsOptionsVisible(true)}
          onVocab={() => {
            setIsVocabSheetVisible(true)
            closeVocab()
          }}
        />
      </main>

      {/* Tooltip */}
      <VocabTooltip
        isSelected={
          !!vocab?.find(
            (v) => v.word.token === vocabWord?.word.token,
          )
        }
        track={vocabEl}
        vocab={vocabWord}
        isLoading={isVocabLoading}
        onClose={() => closeVocab()}
        onAdd={() =>
          handleAddVocab(
            vocabWord &&
              (characterNum ? true : undefined) &&
              ({
                ...vocabWord,
                characterNum,
                numEncounters: 1,
              } as Vocab),
          )
        }
      />

      {isOptionsVisible && (
        <Modal
          visible={isOptionsVisible}
          onClose={() => setIsOptionsVisible(false)}
        >
          {doc && (
            <Options
              doc={doc}
              onChapterNumChange={handleChapterNumChange}
            />
          )}
        </Modal>
      )}

      {vocab && (
        <VocabSheet
          visible={isVocabSheetVisible}
          vocab={vocab}
          onClose={() => setIsVocabSheetVisible(false)}
          onRemove={handleRemoveVocab}
        />
      )}
    </>
  )
}

const DemoReader = () => {
  const { searchParamSession } = useSession()

  const [demoSession, setDemoSession] = useState<Session>(DEMO_SESSION);

  const demoSave = useCallback((s: Partial<Session>) => {
    setDemoSession((ds) => ({ ...ds, ...s }))
  }, [])

  useEffect(() => {
    if (!searchParamSession) return
    demoSave(searchParamSession)
  }, [searchParamSession, demoSave])

  return <Reader session={demoSession} save={demoSave} />
}

const ReaderPage = () => {
  const { isLoggedIn } = useAuth()
  const { session, save } = useSession()

  // Warm up definitions Lambda
  useEffect(() => {
    if (!session?.fromLang || !session?.toLang) return

    defineWord({
      fromLang: session?.fromLang,
      toLang: session?.toLang,
      word: { token: 'lahilo', clean: 'lahilo' },
    })
  }, [session?.fromLang, session?.toLang])

  return (
    <SimpleLayout simple>
      {!session && !isLoggedIn && <DemoReader />}
      {session && <Reader session={session} save={save} />}
      {!session && isLoggedIn && (
        <div className="mx-auto flex w-full max-w-sm justify-center">
          <Spinner />
        </div>
      )}
    </SimpleLayout>
  )
}

export default ReaderPage
